import React from 'react';
import _ from 'lodash';
import moment from 'moment';
import classNames from 'classnames';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import ContextualHelpModal from '../../../../components/ContextualHelpModal';
import LabeledInput from '../../../../components/LabeledInput';
import InfoLabel from '../../../../components/ContextualHelpModal/InfoLabel';
import RelationshipPicker from '../../../../components/Guarantor/RelationshipPicker';
import SubscriberInfo from '../SubscriberInfo';
import { Button } from '../../../../components/Button';
import Enum from '../../../../components/Question/Enum';
import { InfoMessage } from '../../../../components/InfoMessage';
import { InsuranceCardInfo } from '../InsuranceCardInfo';

import * as api from '../../../../api';
import * as store from '../../../../reducers/store';
import {
  cleanInsuranceCoverage,
  convertSubscriberToNamed,
  getBlankSubscriber,
  isValidInsuranceCoverage,
  isValidSubscriber,
  SELF_ONLY_PAYORS,
  showSubscriberToGuarantorRelationshipInput,
} from './utils';
import {
  getEnumOptions,
  getPatientDetails,
  getSubscribersFromGuarantorsPrimitive,
  mergeValues,
} from '../../../../components/Guarantor/utils';
import { isEnumMember } from 'common/dist/utils/types';
import {
  getSelectedForms,
  getSelectedPracticePreference,
} from '../../../../utils/formSet';

import defaultStyles from '../../../../styles/formSetStyles.module.css';
import guarantorStyles from '../../../../components/Guarantor/index.module.css';

type Subscriber = api.PrevisitPatientInsurance.PrevisitSubscriber;

const SUBSCRIBER_HELP_MARKDOWN = `## Who is my subscriber?

The subscriber is the person who pays for health insurance premiums or whose employment is the basis for membership in the insurance plan.`;

interface Props extends StateProps, WithTranslation {
  guarantorValues?: api.Guarantor.PrevisitGuarantorFormFieldValue;
  helpDialog: string | null;
  insuranceInformation: api.PatientInsuranceInformation.InsuranceInformationWithDetails;
  ocrResults?: api.InsuranceCardOcrResult.InsuranceCardOcrResult;
  onSubmit(
    value: api.PrevisitPatientInsurance.PrevisitInsuranceFormFieldValue,
  ): void;
  value: api.PrevisitPatientInsurance.PrevisitInsuranceFormFieldValue;
}

interface StateProps {
  enablePrevisitSubscriberToGuarantorRelationshipCollection: store.Loaded['masterPracticePreference']['enablePrevisitSubscriberToGuarantorRelationshipCollection'];
  formSets: store.Loaded['formSets'];
  forms: store.Forms;
  patient: store.Loaded['loggedInPatient'];
  patientAge?: number;
  employmentStatuses: store.Loaded['masterPracticePreference']['employmentStatuses'];
  previsitPayorDisplayMapping: store.Loaded['masterPracticePreference']['previsitPayorDisplayMapping'];
  patientInsuranceInformationPayors: store.Loaded['masterPracticePreference']['patientInsuranceInformationPayors'];
  patientRelationshipsToSubscriber: store.Loaded['masterPracticePreference']['patientRelationshipsToSubscriber'];
  previsitCollectSubscriberRetirementDateEnabled: store.Loaded['masterPracticePreference']['previsitCollectSubscriberRetirementDateEnabled'];
  previsitMergeSubscriberGuarantorsEnabled: store.Loaded['masterPracticePreference']['previsitMergeSubscriberGuarantorsEnabled'];
  subscriberRelationshipsToGuarantor: store.Loaded['masterPracticePreference']['subscriberRelationshipsToGuarantor'];
  selectedFormSetId: string;
}

interface State {
  subscribersById: Record<string, Subscriber>;
  subscriberOrdering: string[];
  selectedSubscriberId: string | null;
  insuranceCoverage: api.PrevisitPatientInsurance.InsuranceCoverage | null;
  errorMessage: string | null;
  isSubmitting: boolean;
  showHelp: boolean;
}

export class FormDetailsPage extends React.Component<Props, State> {
  state: State = {
    subscriberOrdering: [],
    subscribersById: {},
    selectedSubscriberId: null,
    insuranceCoverage: null,
    errorMessage: null,
    isSubmitting: false,
    showHelp: false,
  };

  componentDidMount() {
    const {
      forms,
      insuranceInformation,
      ocrResults,
      value,
      previsitMergeSubscriberGuarantorsEnabled,
    } = this.props;
    // retrieve potential subscribers from guarantor answers
    let guarantorSubscribers: api.PrevisitPatientInsurance.PrevisitSubscriber[] =
      [];
    if (previsitMergeSubscriberGuarantorsEnabled) {
      guarantorSubscribers = getSubscribersFromGuarantorsPrimitive(forms);
    }
    // TODO: Will need to reconcile subscribers from cards in the future?
    const maybeMergedSubscribers = mergeValues(
      value.subscribers,
      guarantorSubscribers,
    );

    const subscribersById = _.mapKeys(maybeMergedSubscribers, 'id');

    const insuranceCoverage: api.PrevisitPatientInsurance.InsuranceCoverage =
      value.insuranceCoverages?.find(
        ({ id }) => id === insuranceInformation.id,
      ) || {
        id: insuranceInformation.id,
        subscriberId: null,
        payor:
          insuranceInformation.payor ??
          insuranceInformation.ocrInformation?.insuranceProviderName ??
          null,
        primaryPolicyHolderMemberId:
          insuranceInformation.primaryPolicyHolderMemberId ??
          insuranceInformation.ocrInformation?.memberId ??
          null,
        groupNumber:
          insuranceInformation.groupNumber ??
          insuranceInformation.ocrInformation?.groupNumber ??
          null,
        coverageStartDate:
          insuranceInformation.coverageStartDate ??
          (insuranceInformation.ocrInformation?.coverageStartDate
            ? new Date(insuranceInformation.ocrInformation?.coverageStartDate)
            : null),
        primary: false,
      };

    this.setState(
      {
        subscribersById,
        subscriberOrdering: maybeMergedSubscribers.map((g) => g.id),
        selectedSubscriberId: insuranceCoverage?.subscriberId ?? null,
        insuranceCoverage,
      },
      () => {
        if (ocrResults) this.processOcrResults();
      },
    );
  }

  componentDidUpdate(prevProps: Props) {
    const { insuranceCoverage, selectedSubscriberId, subscribersById } =
      this.state;

    if (!prevProps.ocrResults && this.props.ocrResults) {
      // process OCR if it comes in
      this.processOcrResults();
    }

    if (
      !insuranceCoverage?.payor ||
      !SELF_ONLY_PAYORS.has(
        insuranceCoverage.payor as api.PatientInsuranceInformation.Payor,
      ) // TODO: DEPRECATE. See https://app.asana.com/0/0/1203768746570568/f
    ) {
      return;
    }

    const selfSubscriber = _.find(
      subscribersById,
      (s) =>
        s.patientRelationshipToPrimaryPolicyholder ===
        api.Common.Relationship.Self,
    );

    if (!selfSubscriber || selectedSubscriberId === selfSubscriber.id) return;

    this.handleSelectedChange(selfSubscriber.id);
  }

  processOcrResults() {
    const { ocrResults } = this.props;
    const { insuranceCoverage } = this.state;
    if (!insuranceCoverage) return;
    const updateValues: Partial<api.PrevisitPatientInsurance.InsuranceCoverage> =
      {};

    if (
      !insuranceCoverage.payor &&
      ocrResults?.insuranceProviderName &&
      isEnumMember(
        api.PatientInsuranceInformation.Payor,
        ocrResults.insuranceProviderName,
      )
    ) {
      updateValues.payor = ocrResults.insuranceProviderName;
    }

    if (
      !insuranceCoverage.primaryPolicyHolderMemberId &&
      ocrResults?.memberId
    ) {
      updateValues.primaryPolicyHolderMemberId = ocrResults.memberId;
    }

    if (!insuranceCoverage.groupNumber && ocrResults?.groupNumber) {
      updateValues.groupNumber = ocrResults.groupNumber;
    }

    if (!insuranceCoverage.coverageStartDate && ocrResults?.coverageStartDate) {
      const coverageStartDate = new Date(ocrResults.coverageStartDate);
      if (isNaN(coverageStartDate.valueOf())) return;
      updateValues.coverageStartDate = new Date(
        coverageStartDate.getUTCFullYear(),
        coverageStartDate.getUTCMonth(),
        coverageStartDate.getUTCDate(),
      );
    }

    if (_.isEmpty(updateValues)) return;

    this.setState({
      insuranceCoverage: {
        ...insuranceCoverage,
        ...updateValues,
      },
    });
  }

  validate = () => {
    const { insuranceCoverage, subscribersById } = this.state;
    const { formSets, patient, selectedFormSetId } = this.props;
    const encounter = formSets[selectedFormSetId]?.encounter || null;
    if (!insuranceCoverage) return false;

    const validInsuranceCoverage = isValidInsuranceCoverage({
      insuranceCoverage,
      encounter,
      patient,
      onError: (errorMessage) =>
        this.setState({ errorMessage }, () => {
          window.scrollTo(0, 0);
        }),
    });

    if (!validInsuranceCoverage) return false;

    const validSubscriber = isValidSubscriber(
      Object.values(subscribersById),
      insuranceCoverage.subscriberId,
      this.showSubscriberToGuarantorRelationshipInput(),
    );

    if (!validSubscriber) {
      this.setState(
        {
          errorMessage: 'All subscriber information is required.',
        },
        () => {
          window.scrollTo(0, 0);
        },
      );
      return false;
    }

    return true;
  };

  handleSubmit = () => {
    const valid = this.validate();
    if (!valid) return;

    this.setState({ errorMessage: '', isSubmitting: true });

    const { value, insuranceInformation, onSubmit } = this.props;
    const { subscriberOrdering, subscribersById, insuranceCoverage } =
      this.state;
    const insuranceCoverageExists = value.insuranceCoverages?.find(
      (insurance) => insurance.id === insuranceInformation.id,
    );
    const cleanedInsuranceCoverage = cleanInsuranceCoverage(insuranceCoverage!);
    if (!insuranceCoverageExists) {
      onSubmit({
        ...value,
        subscribers: subscriberOrdering
          .map((subId) => {
            const subscriber = subscribersById[subId];
            return {
              ..._.omit(subscriber, 'newlyAdded'),
              // trim string values
              primaryPolicyHolderFirstName:
                subscriber.primaryPolicyHolderFirstName?.trim() ?? null,
              primaryPolicyHolderLastName:
                subscriber.primaryPolicyHolderLastName?.trim() ?? null,
              primaryPolicyHolderEmployer:
                subscriber.primaryPolicyHolderEmployer?.trim() ?? null,
            };
          })
          .filter((subscriber) =>
            // currently selected subscriber should be valid, so clean up invalid entries
            _.every([
              subscriber.primaryPolicyHolderFirstName,
              subscriber.primaryPolicyHolderLastName,
              subscriber.id,
            ]),
          ),
        insuranceCoverages: [
          ...(value.insuranceCoverages || []),
          cleanedInsuranceCoverage,
        ],
      });
      return;
    }
    onSubmit({
      ...value,
      subscribers: subscriberOrdering.map((subId) => {
        const subscriber = subscribersById[subId];
        return {
          ..._.omit(subscriber, 'newlyAdded'),
          // trim string values
          primaryPolicyHolderFirstName:
            subscriber.primaryPolicyHolderFirstName?.trim() ?? null,
          primaryPolicyHolderLastName:
            subscriber.primaryPolicyHolderLastName?.trim() ?? null,
          primaryPolicyHolderEmployer:
            subscriber.primaryPolicyHolderEmployer?.trim() ?? null,
        };
      }),
      insuranceCoverages: (value.insuranceCoverages || []).map((insurance) => {
        if (insurance.id === cleanedInsuranceCoverage.id) {
          return cleanedInsuranceCoverage;
        }
        return insurance;
      }),
    });
  };

  handleSelectedChange = (selectedSubscriberId: string) => {
    const { subscribersById, subscriberOrdering, insuranceCoverage } =
      this.state;

    if (!selectedSubscriberId) {
      return this.setState({ selectedSubscriberId: null });
    }

    const exists = selectedSubscriberId in subscribersById;

    if (exists) {
      return this.setState({
        selectedSubscriberId,
        insuranceCoverage: {
          ...insuranceCoverage!,
          subscriberId: selectedSubscriberId,
        },
      });
    }

    return this.setState({
      selectedSubscriberId,
      subscribersById: {
        ...subscribersById,
        [selectedSubscriberId]: {
          ...getBlankSubscriber(),
          id: selectedSubscriberId,
          newlyAdded: true,
        },
      },
      subscriberOrdering: [...subscriberOrdering, selectedSubscriberId],
      insuranceCoverage: {
        ...insuranceCoverage!,
        subscriberId: selectedSubscriberId,
      },
    });
  };

  updateSubscriber = (subscriberId: string, values: Partial<Subscriber>) => {
    const { subscribersById } = this.state;
    this.setState({
      subscribersById: {
        ...subscribersById,
        [subscriberId]: {
          ...subscribersById[subscriberId],
          ...values,
        },
      },
    });
  };

  memoizedPatientDetails = _.memoize(getPatientDetails);

  memoizedEnumOptions = _.memoize(
    (
      subscriberOrdering: string[],
      subscribersById: Record<string, Subscriber>,
      patientAge?: number,
      payor?: api.PatientInsuranceInformation.Payor | string,
    ) => {
      const selfContact = _.find(
        subscribersById,
        (s) =>
          s.patientRelationshipToPrimaryPolicyholder ===
          api.Common.Relationship.Self,
      );

      const onlySelf =
        Boolean(payor) &&
        SELF_ONLY_PAYORS.has(payor as api.PatientInsuranceInformation.Payor); // TODO: DEPRECATE. See https://app.asana.com/0/0/1203768746570568/f

      return getEnumOptions({
        patientAge,
        onlySelf,
        contacts: subscriberOrdering
          .map((id) => subscribersById[id])
          .filter((item) => !!item)
          .map(convertSubscriberToNamed),
        selfContact: selfContact && convertSubscriberToNamed(selfContact),
      });
    },
    (...args) => _.values(args).join('_'),
  );

  renderSubscriberSelect() {
    const { patientAge, t } = this.props;
    const {
      insuranceCoverage,
      subscriberOrdering,
      subscribersById,
      selectedSubscriberId,
    } = this.state;

    const payor = insuranceCoverage?.payor ?? undefined;
    if (!payor) {
      return null;
    }

    const selfContact = _.find(
      subscribersById,
      (s) =>
        s.patientRelationshipToPrimaryPolicyholder ===
        api.Common.Relationship.Self,
    );

    const { options } = this.memoizedEnumOptions(
      subscriberOrdering,
      subscribersById,
      patientAge,
      payor,
    );

    const hasSelfOption = options.find((o) => o.value === selfContact?.id);
    const optionsLocal = options.map((o) => ({ ...o, display: t(o.display) }));
    return (
      <LabeledInput label={t('Who is the subscriber?')}>
        <Enum
          listClassName={classNames(guarantorStyles.Enum, {
            [guarantorStyles.FirstOptionPatient]: hasSelfOption,
          })}
          options={optionsLocal}
          value={selectedSubscriberId || ''}
          onChange={(value) => {
            this.handleSelectedChange(value);
          }}
        />
      </LabeledInput>
    );
  }

  renderHelpInfo() {
    const { helpDialog, t } = this.props;
    const { insuranceCoverage, showHelp } = this.state;

    const payor = insuranceCoverage?.payor ?? undefined;
    if (!payor) {
      return null;
    }

    const helpText = helpDialog || SUBSCRIBER_HELP_MARKDOWN;

    return (
      <>
        <ContextualHelpModal
          headerText={t('More information')}
          helpText={t(helpText)}
          isOpen={showHelp}
          onRequestClose={() => this.setState({ showHelp: false })}
          onHandleHelpful={async () => this.setState({ showHelp: false })}
        />
        <InfoLabel
          onClick={() => this.setState({ showHelp: true })}
          label={t('Not sure?')}
        />
      </>
    );
  }

  showSubscriberToGuarantorRelationshipInput() {
    const {
      guarantorValues,
      enablePrevisitSubscriberToGuarantorRelationshipCollection,
      subscriberRelationshipsToGuarantor,
    } = this.props;
    const { selectedSubscriberId, subscribersById } = this.state;
    const selectedGuarantor =
      guarantorValues &&
      guarantorValues.guarantors?.find(
        (guarantor) => guarantor.id === guarantorValues.selectedGuarantorId,
      );

    const selectedSubscriber = selectedSubscriberId
      ? subscribersById[selectedSubscriberId]
      : undefined;

    return (
      enablePrevisitSubscriberToGuarantorRelationshipCollection &&
      subscriberRelationshipsToGuarantor &&
      showSubscriberToGuarantorRelationshipInput(
        selectedGuarantor,
        selectedSubscriber,
      )
    );
  }

  renderSubscriberToGuarantorRelationshipInput() {
    const { guarantorValues, subscriberRelationshipsToGuarantor, t } =
      this.props;
    const { selectedSubscriberId, subscribersById } = this.state;

    const selectedSubscriber = selectedSubscriberId
      ? subscribersById[selectedSubscriberId]
      : undefined;
    const selectedGuarantor =
      guarantorValues &&
      guarantorValues.guarantors?.find(
        (guarantor) => guarantor.id === guarantorValues.selectedGuarantorId,
      );

    if (
      !this.showSubscriberToGuarantorRelationshipInput() ||
      !selectedSubscriber
    ) {
      return null;
    }

    return (
      <RelationshipPicker
        mainLabel={null}
        value={selectedSubscriber.primaryPolicyHolderRelationshipToGuarantor}
        relationshipOptions={{
          relationships: subscriberRelationshipsToGuarantor.relationships,
          isReverseRelationship: false,
        }}
        subLabel={`What is ${selectedSubscriber.primaryPolicyHolderFirstName} ${selectedSubscriber.primaryPolicyHolderLastName}'s relationship to ${selectedGuarantor?.firstName} ${selectedGuarantor?.lastName}`}
        onChange={(relationship) => {
          this.updateSubscriber(selectedSubscriber.id, {
            primaryPolicyHolderRelationshipToGuarantor: relationship,
          });
        }}
        primaryEntity="the subscriber"
        associatedEntity={t('thePersonPayingForThisVisit')}
      />
    );
  }

  renderSubscriberForm() {
    const {
      forms,
      helpDialog,
      patient,
      employmentStatuses,
      patientRelationshipsToSubscriber,
      previsitCollectSubscriberRetirementDateEnabled,
    } = this.props;
    const { insuranceCoverage, selectedSubscriberId, subscribersById } =
      this.state;
    if (!insuranceCoverage) return null;

    const { patientAddressDetails, patientCellPhoneNumber } =
      this.memoizedPatientDetails(forms, patient);

    const selectedSubscriber = selectedSubscriberId
      ? subscribersById[selectedSubscriberId]
      : undefined;

    return (
      <>
        {this.renderSubscriberSelect()}
        {this.renderHelpInfo()}

        {selectedSubscriberId && selectedSubscriber && (
          <SubscriberInfo
            employmentStatusOptions={employmentStatuses}
            selectedSubscriber={selectedSubscriber}
            onChange={(values) =>
              this.updateSubscriber(selectedSubscriberId, values)
            }
            patientRelationshipsToOptions={patientRelationshipsToSubscriber}
            collectRetirementDate={
              previsitCollectSubscriberRetirementDateEnabled
            }
            patientAddress={patientAddressDetails}
            patientCellPhoneNumber={patientCellPhoneNumber}
            helpDialog={helpDialog || SUBSCRIBER_HELP_MARKDOWN}
          />
        )}
      </>
    );
  }

  render() {
    const {
      patientInsuranceInformationPayors,
      t,
      previsitPayorDisplayMapping,
    } = this.props;
    const { errorMessage, insuranceCoverage, isSubmitting } = this.state;
    if (!insuranceCoverage) return null;

    return (
      <div>
        {errorMessage && (
          <div className={guarantorStyles.InfoMessage}>
            <InfoMessage isError={true}>{errorMessage}</InfoMessage>
          </div>
        )}
        <div>
          <InsuranceCardInfo
            cardInformation={insuranceCoverage}
            onChange={(values) =>
              this.setState({
                insuranceCoverage: { ...insuranceCoverage, ...values },
              })
            }
            payorOptions={patientInsuranceInformationPayors}
            previsitPayorDisplayMapping={previsitPayorDisplayMapping}
          />
          {this.renderSubscriberForm()}
        </div>

        {this.renderSubscriberToGuarantorRelationshipInput()}

        <div className={defaultStyles.ButtonSubmitWrapper}>
          <Button
            disabled={isSubmitting}
            onClick={this.handleSubmit}
            children={t('Continue')}
            type="button"
          />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: store.FormSetSelected): StateProps => {
  const selectedPractice = getSelectedPracticePreference(state);
  return {
    employmentStatuses: selectedPractice.employmentStatuses,
    patientInsuranceInformationPayors:
      selectedPractice.patientInsuranceInformationPayors,
    patientRelationshipsToSubscriber:
      selectedPractice.patientRelationshipsToSubscriber,
    previsitPayorDisplayMapping: selectedPractice.previsitPayorDisplayMapping,
    enablePrevisitSubscriberToGuarantorRelationshipCollection:
      selectedPractice.enablePrevisitSubscriberToGuarantorRelationshipCollection,
    subscriberRelationshipsToGuarantor:
      selectedPractice.subscriberRelationshipsToGuarantor,
    patient: state.loggedInPatient,
    formSets: state.formSets,
    previsitCollectSubscriberRetirementDateEnabled:
      selectedPractice.previsitCollectSubscriberRetirementDateEnabled,
    previsitMergeSubscriberGuarantorsEnabled:
      selectedPractice.previsitMergeSubscriberGuarantorsEnabled,
    forms: getSelectedForms(state.formSets, state.selectedFormSetId),
    patientAge: state.loggedInPatient?.dateOfBirth
      ? moment().diff(state.loggedInPatient.dateOfBirth, 'years')
      : undefined,
    selectedFormSetId: state.selectedFormSetId,
  };
};

export default withTranslation()(connect(mapStateToProps)(FormDetailsPage));
